// components/Snowfall.js
import React, { useMemo } from 'react';

const Snowfall = ({ show }) => {
  const { frontSnowflakes, backSnowflakes } = useMemo(() => {
    // Base snowflake generator
    const generateBaseSnowflake = (id) => ({
      id,
      left: `${Math.random() * 100}%`,
      size: Math.random() * 0.5 + 0.2,
      animationDuration: `${Math.random() * 8 + 7}s`,
      animationDelay: `${Math.random() * 3}s`,
      startingHeight: -(Math.random() * 100)
    });

    // Generate front snowflakes with higher opacity
    const generateFrontSnowflake = (id) => ({
      ...generateBaseSnowflake(`front-${id}`),
      opacity: Math.random() * 0.4 + 0.6, // Range: 0.6 to 1.0
    });

    // Generate back snowflakes with lower opacity
    const generateBackSnowflake = (id) => ({
      ...generateBaseSnowflake(`back-${id}`),
      opacity: Math.random() * 0.6 + 0.2, // Range: 0.2 to 0.6
    });

    // Create two arrays of snowflakes with different opacities
    const front = Array.from({ length: 15 }, (_, i) => generateFrontSnowflake(i));
    const back = Array.from({ length: 50 }, (_, i) => generateBackSnowflake(i));

    return { frontSnowflakes: front, backSnowflakes: back };
  }, []);

  const SnowflakeLayer = ({ snowflakes, zIndex }) => (
    <div className={`absolute inset-0 ${zIndex}`}>
      {snowflakes.map(flake => (
        <div
          key={flake.id}
          className="absolute animate-snowfall"
          style={{
            left: flake.left,
            width: `${flake.size}rem`,
            height: `${flake.size}rem`,
            animationDuration: flake.animationDuration,
            animationDelay: flake.animationDelay,
            '--snow-opacity': flake.opacity,
            '--start-height': `${flake.startingHeight}vh`
          }}
        >
          ❄
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className={`transition-opacity duration-2000 ${show ? 'opacity-100' : 'opacity-0'}`}>
        {/* Back layer */}
        <SnowflakeLayer snowflakes={backSnowflakes} zIndex="z-0" />
        {/* Front layer - will appear above the card */}
        <SnowflakeLayer snowflakes={frontSnowflakes} zIndex="z-20" />
      </div>

      <style>{`
        @keyframes snowfall {
          0% {
            transform: translateY(var(--start-height)) rotate(0deg);
            opacity: 0;
          }
          5% {
            opacity: var(--snow-opacity);
          }
          95% {
            opacity: var(--snow-opacity);
          }
          100% {
            transform: translateY(110vh) rotate(360deg);
            opacity: 0;
          }
        }
        .animate-snowfall {
          animation: snowfall linear infinite;
          opacity: 0;
        }
      `}</style>
    </>
  );
};

export default React.memo(Snowfall);